import React, { useState, useEffect } from "react";

export type CalendarType = "day" | "week" | "month" | "6m" | "year";

interface CalendarPickerProps {
  type: CalendarType;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  inModal?: boolean;
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  type,
  selectedDate,
  onDateSelect,
  isOpen,
  onClose,
  className = "",
  inModal = false,
}) => {
  const [currentViewDate, setCurrentViewDate] = useState(selectedDate);

  // Sync currentViewDate with selectedDate when component opens
  useEffect(() => {
    if (isOpen) {
      setCurrentViewDate(selectedDate);
    }
  }, [isOpen, selectedDate]);

  // Helper functions
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Week boundary functions (only used for week type)
  const getWeekStartDate = (date: Date): Date => {
    const newDate = new Date(date);
    const dayOfWeek = date.getDay();
    // Sunday = 0, Monday = 1, etc. We want to start from Sunday
    newDate.setDate(date.getDate() - dayOfWeek);
    return newDate;
  };

  const getWeekEndDate = (date: Date): Date => {
    const newDate = new Date(date);
    const dayOfWeek = date.getDay();
    // Saturday = 6, so we add (6 - dayOfWeek) to get to Saturday
    newDate.setDate(date.getDate() + (6 - dayOfWeek));
    return newDate;
  };

  // Check if date is in current selection based on type
  const isInCurrentSelection = (date: Date): boolean => {
    switch (type) {
      case "day":
        return date.getTime() === selectedDate.getTime();
      case "week": {
        const weekStart = getWeekStartDate(selectedDate);
        const weekEnd = getWeekEndDate(selectedDate);
        return date >= weekStart && date <= weekEnd;
      }
      case "month":
        return (
          date.getMonth() === selectedDate.getMonth() &&
          date.getFullYear() === selectedDate.getFullYear()
        );

      case "6m": {
        const month = date.getMonth();
        const isFirstHalf = selectedDate.getMonth() <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)
        if (isFirstHalf) {
          return month >= 0 && month <= 5; // Jan-Jun
        } else {
          return month >= 6 && month <= 11; // Jul-Dec
        }
      }
      case "year":
        return date.getFullYear() === selectedDate.getFullYear();
      default:
        return false;
    }
  };

  // Check if date is selection boundary
  const isSelectionBoundary = (date: Date): boolean => {
    switch (type) {
      case "day":
        return date.getTime() === selectedDate.getTime();
      case "week": {
        const weekStart = getWeekStartDate(selectedDate);
        const weekEnd = getWeekEndDate(selectedDate);
        return (
          date.getTime() === weekStart.getTime() ||
          date.getTime() === weekEnd.getTime()
        );
      }
      case "month":
        return (
          date.getDate() === 1 ||
          date.getDate() === getDaysInMonth(date.getFullYear(), date.getMonth())
        );
      case "6m": {
        const month = date.getMonth();
        const isFirstHalf = selectedDate.getMonth() <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)
        if (isFirstHalf) {
          return (
            (month === 0 && date.getDate() === 1) || // Jan 1st
            (month === 5 &&
              date.getDate() === getDaysInMonth(date.getFullYear(), 5))
          ); // Jun 30th
        } else {
          return (
            (month === 6 && date.getDate() === 1) || // Jul 1st
            (month === 11 &&
              date.getDate() === getDaysInMonth(date.getFullYear(), 11))
          ); // Dec 31st
        }
      }
      case "year":
        return (
          (date.getMonth() === 0 && date.getDate() === 1) ||
          (date.getMonth() === 11 && date.getDate() === 31)
        );
      default:
        return false;
    }
  };

  const renderCalendar = () => {
    const year = currentViewDate.getFullYear();
    const month = currentViewDate.getMonth();

    // For month type, show month picker
    if (type === "month") {
      return (
        <div
          className={`${inModal ? 'relative' : 'absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 transform'} w-full max-w-sm rounded-lg border border-gray-600 bg-gray-800 p-4 shadow-xl ${className}`}
        >
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => {
                const prevYear = new Date(year - 1, month, 1);
                setCurrentViewDate(prevYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              ←
            </button>
            <div className="text-center">
              <div className="text-lg font-medium text-white">{year}</div>
            </div>
            <button
              onClick={() => {
                const nextYear = new Date(year + 1, month, 1);
                setCurrentViewDate(nextYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {Array.from({ length: 12 }, (_, i) => {
              const monthDate = new Date(year, i, 1);
              const isSelected = month === i;

              return (
                <button
                  key={i}
                  onClick={() => {
                    onDateSelect(monthDate);
                    onClose();
                  }}
                  className={`rounded p-2 sm:p-3 text-sm font-medium transition-colors ${isSelected
                    ? "bg-blue-500 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                >
                  {new Date(year, i, 1).toLocaleDateString("en-US", {
                    month: "short",
                  })}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // For year type, show 10 years (current year + 9 previous years)
    if (type === "year") {
      // Show current year plus 9 previous years (no future years)
      const currentYear = new Date().getFullYear();
      const viewYear = currentViewDate.getFullYear();

      // Calculate the 10-year block that contains the viewYear
      // We want to show 10 years in blocks
      // If viewYear is 2025, show 2016-2025
      // If viewYear is 2015, show 2006-2015
      // If viewYear is 2006, show 2006-2015
      const yearStart = Math.max(viewYear, 0);
      const yearEnd = yearStart + 9;


      return (
        <div
          className={`${inModal ? 'relative' : 'absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 transform'} min-w-[320px] rounded-lg border border-gray-600 bg-gray-800 p-6 shadow-xl ${className}`}
        >
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => {
                // Move to previous 10-year block
                const prevYearStart = Math.max(yearStart - 10, 0);
                const prevYear = new Date(prevYearStart, 0, 1);
                setCurrentViewDate(prevYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              ←
            </button>
            <div className="text-center">
              <div className="text-lg font-medium text-white">
                {yearStart} - {yearEnd}
              </div>
            </div>
            <button
              onClick={() => {
                // Move to next 10-year block
                const nextYearStart = yearStart + 10;
                const nextYear = new Date(nextYearStart, 0, 1);
                setCurrentViewDate(nextYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1 sm:gap-2">
            {Array.from({ length: 10 }, (_, i) => {
              const yearValue = yearStart + i;
              const isSelected = yearValue === selectedDate.getFullYear();
              const isCurrentYear = yearValue === currentYear;

              return (
                <button
                  key={yearValue}
                  onClick={() => {
                    const yearDate = new Date(yearValue, 0, 1);
                    onDateSelect(yearDate);
                    onClose();
                  }}
                  className={`flex items-center justify-center rounded p-3 text-sm font-medium transition-colors ${isSelected
                    ? "bg-blue-500 text-white"
                    : isCurrentYear
                      ? "bg-green-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                >
                  {yearValue}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // For 6-month type, show Jan-Jun vs Jul-Dec selection
    if (type === "6m") {
      const isFirstHalf = month <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)

      return (
        <div
          className={`${inModal ? 'relative' : 'absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 transform'} w-full max-w-sm rounded-lg border border-gray-600 bg-gray-800 p-4 shadow-xl ${className}`}
        >
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => {
                const prevYear = new Date(year - 1, month, 1);
                setCurrentViewDate(prevYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              ←
            </button>
            <div className="text-center">
              <div className="text-lg font-medium text-white">{year}</div>
            </div>
            <button
              onClick={() => {
                const nextYear = new Date(year + 1, month, 1);
                setCurrentViewDate(nextYear);
              }}
              className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Jan-Jun */}
            <button
              onClick={() => {
                const janDate = new Date(year, 0, 1); // January 1st
                onDateSelect(janDate);
                onClose();
              }}
              className={`rounded p-4 text-sm font-medium transition-colors ${isFirstHalf
                ? "bg-blue-500 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
            >
              <div className="text-lg font-semibold">Jan-Jun</div>
              <div className="text-xs opacity-80">First Half</div>
            </button>

            {/* Jul-Dec */}
            <button
              onClick={() => {
                const julDate = new Date(year, 6, 1); // July 1st
                onDateSelect(julDate);
                onClose();
              }}
              className={`rounded p-4 text-sm font-medium transition-colors ${!isFirstHalf
                ? "bg-blue-500 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
            >
              <div className="text-lg font-semibold">Jul-Dec</div>
              <div className="text-xs opacity-80">Second Half</div>
            </button>
          </div>
        </div>
      );
    }

    // For day and week types, show the full calendar grid
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    const days = [];

    // Add days from previous month (with reduced opacity)
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const prevMonthDays = getDaysInMonth(prevYear, prevMonth);
      const day = prevMonthDays - i;
      const prevMonthDate = new Date(prevYear, prevMonth, day);

      days.push(
        <button
          key={`prev-${day}`}
          onClick={() => {
            onDateSelect(prevMonthDate);
            onClose();
          }}
          className="flex h-8 w-8 sm:h-10 sm:w-10 cursor-pointer items-center justify-center text-sm font-medium text-gray-500 opacity-40 transition-opacity hover:opacity-60"
        >
          {day}
        </button>,
      );
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const isInSelection = isInCurrentSelection(currentDate);
      const isBoundary = isSelectionBoundary(currentDate);

      let bgClass = "";
      let textClass = "text-white";

      if (isInSelection) {
        if (isBoundary) {
          bgClass = "bg-blue-500"; // Solid blue for boundaries
        } else {
          bgClass = "bg-blue-500"; // Solid blue for month/year selection
          textClass = "text-white";
        }
      } else {
        bgClass = "bg-gray-700"; // Dark gray for unselected
      }

      days.push(
        <button
          key={day}
          onClick={() => {
            onDateSelect(currentDate);
            onClose();
          }}
          className={`flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded text-sm font-medium transition-colors ${bgClass} ${textClass} hover:opacity-80`}
        >
          {day}
        </button>,
      );
    }

    // Add days from next month (with reduced opacity) to fill the grid
    const totalCells = 42; // 6 rows × 7 columns
    const remainingCells = totalCells - days.length;

    for (let i = 1; i <= remainingCells; i++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const nextMonthDate = new Date(nextYear, nextMonth, i);

      days.push(
        <button
          key={`next-${i}`}
          onClick={() => {
            onDateSelect(nextMonthDate);
            onClose();
          }}
          className="flex h-8 w-8 sm:h-10 sm:w-10 cursor-pointer items-center justify-center text-sm font-medium text-gray-500 opacity-40 transition-opacity hover:opacity-60"
        >
          {i}
        </button>,
      );
    }

    return (
      <div
        className={`${inModal ? 'relative' : 'absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 transform'} min-w-[320px] rounded-lg border border-gray-600 bg-gray-800 p-6 shadow-xl ${className}`}
      >
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => {
              const prevMonth = new Date(year, month - 1, 1);
              setCurrentViewDate(prevMonth);
            }}
            className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
          >
            ←
          </button>
          <div className="text-center">
            <div className="text-lg font-medium text-white">
              {new Date(year, month).toLocaleDateString("en-US", {
                month: "long",
              })}
            </div>
            <div className="text-sm text-gray-400">
              {new Date(year, month).getFullYear()}
            </div>
          </div>
          <button
            onClick={() => {
              const nextMonth = new Date(year, month + 1, 1);
              setCurrentViewDate(nextMonth);
            }}
            className="rounded p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
          >
            →
          </button>
        </div>

        <div className="mb-3 grid grid-cols-7 gap-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div
              key={day}
              className="flex h-10 w-10 items-center justify-center text-xs font-medium text-gray-400"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">{days}</div>
      </div>
    );
  };

  if (!isOpen) return null;

  return renderCalendar();
};
